import { all, call, fork, put, takeEvery, select } from "redux-saga/effects";
import ChannelService from "../../services/Channel";
import {
  CHANNEL_CREATE,
  CHANNEL_UPDATE,
  CHANNEL_GET_ALL,
  CHANNEL_DELETE
} from "../../constants/ActionTypes";
import {
  CHANNEL_ACCESSIBLE_GET_ALL,
  CHANNEL_ACCESSIBLE_GET_ALL_SUCCESS,
  CHANNEL_ACCESSIBLE_GET_ALL_ERROR
} from "../reducers/Channel";
import {
  channelCreateSuccess,
  channelCreateError,
  channelUpdateSuccess,
  channelUpdateError,
  channelGetAllSuccess,
  channelDeleteSuccess,
  channelDeleteError,
  channelDetailHide,
  channelModalVisible,
  channelComponentVisible
} from "../actions/Channel";
import { buildCompanyScopeParams } from "../reducers/Filter";
import { unloadingButton, loadingButton } from "../actions/loading";
import { notificationMessage } from "../reducers/FeedbackMessage";

const getCompanyFilter = state => state.filter && state.filter.companyId;

const createChannelRequest = async payload =>
  await ChannelService.create(payload)
    .then(channel => channel)
    .catch(error => {
      throw error;
    });

const updateChannelRequest = async payload =>
  await ChannelService.update(payload)
    .then(channel => channel)
    .catch(error => {
      throw error;
    });

const deleteChannelRequest = async payload =>
  await ChannelService.delete(payload)
    .then(channel => channel)
    .catch(error => {
      throw error;
    });

const getAllChannelsRequest = async params =>
  await ChannelService.index(params)
    .then(channel => channel)
    .catch(error => {
      throw error;
    });

const getAccessibleChannelsRequest = async params =>
  await ChannelService.accessible(params)
    .then(channel => channel)
    .catch(error => {
      throw error;
    });

function* refreshChannelsWithCurrentFilter() {
  const companyId = yield select(getCompanyFilter);
  const params = buildCompanyScopeParams(companyId);
  const channels = yield call(getAllChannelsRequest, params);

  if (channels) {
    yield put(channelComponentVisible(false));
    yield put(channelGetAllSuccess(channels));
  }
}

function* createChannel(payload) {
  yield put(loadingButton());
  try {
    yield call(createChannelRequest, payload);

    yield put(unloadingButton());
    yield put(
      notificationMessage("notifications.channel.create.success", "success")
    );
    yield put(channelModalVisible(false, false));
    yield put(channelCreateSuccess());
    try {
      yield call(refreshChannelsWithCurrentFilter);
    } catch (error) {
      yield put(
        notificationMessage("notifications.channel.create.error", "error")
      );
      yield put(
        notificationMessage("notifications.channel.list.error", "error")
      );
    }
  } catch (error) {
    yield put(unloadingButton());
    yield put(
      notificationMessage("notifications.channel.create.error", "error")
    );
    yield put(channelCreateError(error));
  }
}

function* deleteChannel(payload) {
  try {
    yield call(deleteChannelRequest, payload);

    yield put(
      notificationMessage("notifications.channel.delete.success", "success")
    );
    yield put(channelDeleteSuccess());
    yield put(channelDetailHide());
    try {
      yield call(refreshChannelsWithCurrentFilter);
    } catch (error) {
      yield put(
        notificationMessage("notifications.channel.list.error", "error")
      );
    }
  } catch (error) {
    yield put(
      notificationMessage("notifications.channel.delete.error", "error")
    );
    yield put(channelDeleteError(error));
  }
}

function* updateChannel(payload) {
  yield put(loadingButton());
  try {
    const channelUpdated = yield call(updateChannelRequest, payload);
    yield put(unloadingButton());
    yield put(
      notificationMessage("notifications.channel.update.success", "success")
    );
    yield put(channelModalVisible(false, false));
    yield put(channelUpdateSuccess(channelUpdated));
    try {
      yield call(refreshChannelsWithCurrentFilter);
    } catch (error) {
      yield put(
        notificationMessage("notifications.channel.list.error", "error")
      );
    }
  } catch (error) {
    yield put(unloadingButton());
    yield put(
      notificationMessage("notifications.channel.update.error", "error")
    );
    yield put(channelUpdateError(error));
  }
}

function* getAllChannel({ payload }) {
  try {
    yield put(channelComponentVisible(true));
    const params = buildCompanyScopeParams(payload);
    const channels = yield call(getAllChannelsRequest, params);
    if (channels) {
      yield put(channelComponentVisible(false));
      yield put(channelGetAllSuccess(channels));
    }
  } catch (error) {
    yield put(channelComponentVisible(false));
  }
}

function* getAllAccessibleChannels({ payload }) {
  try {
    const params = buildCompanyScopeParams(payload);
    const channels = yield call(getAccessibleChannelsRequest, params);
    yield put({ type: CHANNEL_ACCESSIBLE_GET_ALL_SUCCESS, payload: channels });
  } catch (error) {
    yield put({ type: CHANNEL_ACCESSIBLE_GET_ALL_ERROR, payload: error });
  }
}

export function* channelCreateSaga() {
  yield takeEvery(CHANNEL_CREATE, createChannel);
}

export function* channelDeleteSaga() {
  yield takeEvery(CHANNEL_DELETE, deleteChannel);
}

export function* channelUpdateSaga() {
  yield takeEvery(CHANNEL_UPDATE, updateChannel);
}

export function* channelGetAllSaga() {
  yield takeEvery(CHANNEL_GET_ALL, getAllChannel);
}

export function* channelAccessibleGetAllSaga() {
  yield takeEvery(CHANNEL_ACCESSIBLE_GET_ALL, getAllAccessibleChannels);
}

export default function* rootSaga() {
  yield all([
    fork(channelCreateSaga),
    fork(channelUpdateSaga),
    fork(channelDeleteSaga),
    fork(channelGetAllSaga),
    fork(channelAccessibleGetAllSaga)
  ]);
}
